import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Copy, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import Button from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';

export type TaxMalaysiaFormState = {
  fullName: string;
  myKadOrTin: string;
  phone: string;
  email: string;
  businessName: string;
  ssmNo: string;
  yearOfAssessment: string;
  grossCashIncomeRm: string;
  grossBarterIncomeRm: string;
  platformIncomeRm: string;
  brandDealsRm: string;
  otherIncomeRm: string;
  expensesRm: string;
  notes: string;
};

const defaultForm = (): TaxMalaysiaFormState => ({
  fullName: '',
  myKadOrTin: '',
  phone: '',
  email: '',
  businessName: '',
  ssmNo: '',
  yearOfAssessment: String(new Date().getFullYear()),
  grossCashIncomeRm: '',
  grossBarterIncomeRm: '',
  platformIncomeRm: '',
  brandDealsRm: '',
  otherIncomeRm: '',
  expensesRm: '',
  notes: '',
});

const LHDN_MYTAX = 'https://mytax.hasil.gov.my/';
const LHDN_PORTAL = 'https://www.hasil.gov.my/en/individual/';

function parseMoney(s: string): number {
  const t = s.replace(/,/g, '').trim();
  if (t === '') return 0;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n: number): string {
  return n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function TaxSubmission() {
  const { user } = useAuthStore();
  const [form, setForm] = useState<TaxMalaysiaFormState>(() => defaultForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const { data } = await api.get<{
          latest: { createdAt: string } | null;
          payload: Partial<TaxMalaysiaFormState> | null;
        }>('/users/me/tax-malaysia-worksheet/latest');
        if (cancelled) return;
        const base = defaultForm();
        if (data?.payload && typeof data.payload === 'object') {
          setForm({ ...base, ...data.payload });
        } else {
          setForm({
            ...base,
            fullName: user?.fullName || base.fullName,
            email: user?.email || base.email,
            phone: user?.phone || base.phone,
          });
        }
        if (data?.latest?.createdAt) {
          setSavedAt(data.latest.createdAt);
        }
      } catch (e: unknown) {
        if (cancelled) return;
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        setError(msg || 'Could not load your saved worksheet.');
        setForm((f) => ({
          ...defaultForm(),
          ...f,
          fullName: user?.fullName || f.fullName,
          email: user?.email || f.email,
          phone: user?.phone || f.phone,
        }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totals = useMemo(() => {
    const cash = parseMoney(form.grossCashIncomeRm);
    const barter = parseMoney(form.grossBarterIncomeRm);
    const platform = parseMoney(form.platformIncomeRm);
    const brands = parseMoney(form.brandDealsRm);
    const other = parseMoney(form.otherIncomeRm);
    const expenses = parseMoney(form.expensesRm);
    const gross = cash + barter + platform + brands + other;
    const net = Math.max(0, gross - expenses);
    return { cash, barter, platform, brands, other, expenses, gross, net };
  }, [form]);

  const copyBlock = useMemo(() => {
    const lines = [
      '--- kol.my — LHDNM / MyTax helper (not tax advice) ---',
      `Year of assessment (YA): ${form.yearOfAssessment || '(fill)'}`,
      `Full name: ${form.fullName || '(fill)'}`,
      `MyKad / TIN: ${form.myKadOrTin || '(fill)'}`,
      `Phone: ${form.phone || '(fill)'}`,
      `Email: ${form.email || '(fill)'}`,
      `Trade / creator name (if any): ${form.businessName || '(optional)'}`,
      `SSM no. (if registered): ${form.ssmNo || '(optional)'}`,
      '',
      'Income summary (RM) — align with LHDNM guidance for social media / KOL income:',
      `  Cash / banked creator income: ${formatMoney(totals.cash)}`,
      `  Barter / gifts / in-kind (fair value): ${formatMoney(totals.barter)}`,
      `  Platform payouts (TikTok, YouTube, Meta, etc.): ${formatMoney(totals.platform)}`,
      `  Brand / agency deals (kol.my & others): ${formatMoney(totals.brands)}`,
      `  Other taxable receipts: ${formatMoney(totals.other)}`,
      `  Gross receipts subtotal: ${formatMoney(totals.gross)}`,
      `  Allowable business expenses (keep receipts): ${formatMoney(totals.expenses)}`,
      `  Net indicator (gross − expenses): ${formatMoney(totals.net)}`,
      '',
      'Notes for your accountant / e-Filing:',
      form.notes || '(add notes)',
      '',
      'Official: register & file via MyTax at hasil.gov.my. Verify deadlines in the current LHDNM filing programme.',
    ];
    return lines.join('\n');
  }, [form, totals]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(copyBlock);
    } catch {
      window.prompt('Copy this text:', copyBlock);
    }
  };

  const set =
    (key: keyof TaxMalaysiaFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
    };

  const saveWorksheet = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.post<{ id: string; snapshot: { createdAt: string } }>(
        '/users/me/tax-malaysia-worksheet',
        { payload: form }
      );
      const when = data?.snapshot?.createdAt ?? new Date().toISOString();
      setSavedAt(when);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setError(msg || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center text-glass-text-2 text-sm">Loading your worksheet…</div>
    );
  }

  return (
    <div className="ph-no-capture max-w-3xl mx-auto space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold text-glass-text flex items-center gap-2">
          <FileSpreadsheet className="w-7 h-7 text-accent2" />
          Tax submission helper (Malaysia)
        </h1>
        <p className="text-glass-text-2 mt-2 text-sm leading-relaxed">
          Lembaga Hasil Dalam Negeri Malaysia (LHDNM) expects income from social media and brand work to be reported
          when you are taxable — including small amounts and non-cash benefits at fair value. This page is a free
          worksheet: it does not file your return and is not legal or tax advice. Use{' '}
          <a href={LHDN_MYTAX} className="text-accent2 underline inline-flex items-center gap-0.5" target="_blank" rel="noreferrer">
            MyTax <ExternalLink className="w-3 h-3 shrink-0" />
          </a>{' '}
          for official registration and e-Filing; confirm categories with Form B (or your accountant).
        </p>
        {savedAt ? (
          <p className="text-xs text-glass-text-3 mt-2">
            Last saved: {new Date(savedAt).toLocaleString()}
          </p>
        ) : (
          <p className="text-xs text-glass-text-3 mt-2">Not saved to your account yet — use Save worksheet below.</p>
        )}
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-200">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <Button type="button" onClick={saveWorksheet} disabled={saving} className="inline-flex items-center gap-2">
          {saving ? 'Saving…' : 'Save worksheet'}
        </Button>
        <p className="text-xs text-glass-text-3 self-center">
          Each save stores a snapshot (totals + fields) for your records and future stats. Not a tax filing.
        </p>
      </div>

      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="pt-6 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-glass-text-2 space-y-2">
            <p>
              <strong className="text-glass-text">Deadlines</strong> change each year. For YA 2025, many sources cite
              manual Form B by 30 June and e-Filing by mid-July — always check the current{' '}
              <a href={LHDN_PORTAL} className="text-accent2 underline" target="_blank" rel="noreferrer">
                LHDNM filing programme
              </a>
              . If you were told 15 May for a specific instalment or estimate (e.g. CP500), confirm the date on your
              LHDNM notice or ask a tax agent.
            </p>
            <p>
              Influencer / KOL income may be business income (Section 4(a)) or other income depending on your facts;
              keep records at least seven years. See LHDNM&apos;s &quot;Guidelines on Tax Treatment on Income of Social
              Media Influencer&quot; (2026) and related notices.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your details</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs text-glass-text-2">Full name (as per MyKad / tax record)</label>
            <Input value={form.fullName} onChange={set('fullName')} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">MyKad no. / TIN</label>
            <Input value={form.myKadOrTin} onChange={set('myKadOrTin')} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Year of assessment (YA)</label>
            <Input value={form.yearOfAssessment} onChange={set('yearOfAssessment')} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Phone</label>
            <Input value={form.phone} onChange={set('phone')} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Email</label>
            <Input type="email" value={form.email} onChange={set('email')} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Trade / creator name (optional)</label>
            <Input value={form.businessName} onChange={set('businessName')} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">SSM registration no. (if any)</label>
            <Input value={form.ssmNo} onChange={set('ssmNo')} className="mt-1" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Income & expenses (RM)</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-glass-text-2">Cash / banked creator income</label>
            <Input inputMode="decimal" value={form.grossCashIncomeRm} onChange={set('grossCashIncomeRm')} className="mt-1" placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Barter / gifts / in-kind (fair value)</label>
            <Input inputMode="decimal" value={form.grossBarterIncomeRm} onChange={set('grossBarterIncomeRm')} className="mt-1" placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Platform payouts</label>
            <Input inputMode="decimal" value={form.platformIncomeRm} onChange={set('platformIncomeRm')} className="mt-1" placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Brand & agency deals</label>
            <Input inputMode="decimal" value={form.brandDealsRm} onChange={set('brandDealsRm')} className="mt-1" placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Other taxable receipts</label>
            <Input inputMode="decimal" value={form.otherIncomeRm} onChange={set('otherIncomeRm')} className="mt-1" placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs text-glass-text-2">Allowable business expenses (total)</label>
            <Input inputMode="decimal" value={form.expensesRm} onChange={set('expensesRm')} className="mt-1" placeholder="0.00" />
          </div>
          <div className="sm:col-span-2 rounded-lg border border-glass bg-glass-2/40 p-4 text-sm">
            <div className="flex justify-between text-glass-text">
              <span>Gross receipts (sum of income lines)</span>
              <span className="font-mono">RM {formatMoney(totals.gross)}</span>
            </div>
            <div className="flex justify-between text-glass-text-2 mt-1">
              <span>Minus expenses (indicator only)</span>
              <span className="font-mono">RM {formatMoney(totals.expenses)}</span>
            </div>
            <div className="flex justify-between text-glass-text font-medium mt-2 pt-2 border-t border-glass">
              <span>Net indicator</span>
              <span className="font-mono">RM {formatMoney(totals.net)}</span>
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-glass-text-2">Notes for filing (CP502, CP204, references, etc.)</label>
            <Textarea value={form.notes} onChange={set('notes')} className="mt-1" rows={4} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Copy-paste pack for MyTax / Form B</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-glass-text-2">
            Paste into Notepad first, then transfer each figure into the matching field in MyTax. Field names on the
            official site may differ; map by meaning (business receipts, P&amp;L, balance sheet if applicable).
          </p>
          <Button type="button" onClick={copyToClipboard} className="inline-flex items-center gap-2">
            <Copy className="w-4 h-4" />
            Copy all
          </Button>
          <pre className="text-xs font-mono whitespace-pre-wrap bg-glass-2/50 border border-glass rounded-lg p-4 max-h-72 overflow-auto text-glass-text">
            {copyBlock}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
